import { useState } from "react";
import { useForm } from "react-hook-form";
import { FormContainer } from "@/components/form/form-container";
import { FieldMultiSelect } from "@/components/form/field-multi-select";
import { DefaultButton } from "@/components/ui/button";
import { useRFSStatusCount } from "@/api/rfs";
import { ROLE, STATUS } from "@/utils/constant";
import { H4 } from "@/components/ui/typography";
import DataCard from "./DataCard";
import RFSTrendingSubmission from "./RFSTrendingSubmission";
import { useFormOptions } from "./hooks/useFormOptions";
import { FieldInputDate } from "@/components/form/field-input-date";
import { useAuth } from "@/features/auth/useAuth";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";
import { useProfileByKeycloakId } from "@/api/auth";
import RFSStatisticDownloadSection from "./RFSStatisticDownloadSection";

interface FormSchema {
  picLinknet: string[];
  picVendor: string[];
  tenantName: string[];
  vendorName: string[];
  dateMonthYear: string | null;
}
export function RFSStatistics() {
  const [selectedPICLinknets, setSelectedPICLinknets] = useState("");
  const [selectedPICVendors, setSelectedPICVendors] = useState("");
  const [selectedTenantNames, setSelectedTenantNames] = useState("");
  const [selectedVendorNames, setSelectedVendorNames] = useState("");
  const [selectedDateMonthYear, setSelectedDateMonthYear] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  });

  const auth = useAuth();
  const user = auth.getUser();
  const { data: profile } = useProfileByKeycloakId({ keycloakId: user?.id });
  const isVendor =
    user?.roles?.includes(ROLE.VENDOR) &&
    !hasCommonElement(user?.roles ?? [], [
      ROLE.ADMINISTRATOR,
      ROLE.VALIDATOR_DOCUMENT_OFFICER,
      ROLE.REVISE_DOCUMENT_OFFICER,
      ROLE.MONITORING_TEAM,
    ]);

  const form = useForm<FormSchema>({
    defaultValues: {
      dateMonthYear: new Date().toISOString().substring(0, 10),
    },
  });

  const { data: rfsStatusCount } = useRFSStatusCount({
    picLinknet: selectedPICLinknets,
    picVendor: selectedPICVendors,
    tenantName: selectedTenantNames,
    vendorName: isVendor ? profile?.companyCode : selectedVendorNames,
    dateMonthYear: selectedDateMonthYear,
  });

  const {
    picLinknetOptions,
    picVendorOptions,
    vendorNameOptions,
    tenantsNameOptions,
  } = useFormOptions();

  function handleFilter(values: FormSchema) {
    setSelectedPICLinknets((values.picLinknet ?? []).join(","));
    setSelectedPICVendors((values.picVendor ?? []).join(","));
    setSelectedTenantNames((values.tenantName ?? []).join(","));
    setSelectedVendorNames((values.vendorName ?? []).join(","));
    setSelectedDateMonthYear(
      values.dateMonthYear ? values.dateMonthYear.substring(0, 7) : "",
    );
  }

  return (
    <>
      <div className="flex items-end justify-between">
        <FormContainer
          form={form}
          onSubmit={handleFilter}
          className="flex items-end gap-x-2"
        >
          <FieldMultiSelect
            name="picLinknet"
            label="PIC Linknet"
            title="PIC Linknet"
            options={picLinknetOptions ?? []}
            hideLabel
          />
          <FieldMultiSelect
            name="picVendor"
            label="PIC Vendor"
            title="PIC Vendor"
            options={picVendorOptions ?? []}
            hideLabel
          />
          <FieldMultiSelect
            name="vendorName"
            label="Vendor Name"
            title="Vendor Name"
            options={vendorNameOptions ?? []}
            isDisabled={isVendor}
            hideLabel
          />
          <FieldMultiSelect
            name="tenantName"
            label="Tenant Name"
            title="Tenant Name"
            options={tenantsNameOptions ?? []}
            hideLabel
          />
          <FieldInputDate
            name="dateMonthYear"
            label="Date Month Year"
            placeholder="Filter Month"
            dateFormat="MMMM yyyy"
            classNameContainer="w-fit"
            className="h-9 w-56 border-axiata-blue"
            clearButtonClassName="top-2"
            showFullMonthYearPicker
            showMonthYearPicker
            hideLabel
          />
          <DefaultButton mood="axiata-blue" type="submit">
            Filter
          </DefaultButton>
        </FormContainer>

        <RFSStatisticDownloadSection
          selectedPICLinknets={selectedPICLinknets}
          selectedPICVendors={selectedPICVendors}
          selectedTenantNames={selectedTenantNames}
          selectedVendorNames={selectedVendorNames}
          selectedDateMonthYear={selectedDateMonthYear}
          isVendor={isVendor}
          companyCode={profile?.companyCode}
        />
      </div>

      <H4>RFS Submission Status</H4>
      <ol className="flex flex-wrap items-center justify-center gap-4">
        <div className="flex w-full flex-col gap-4">
          {/* Row 1 - Bagian Total Bucket Tenant */}
          <div className="flex w-full gap-4">
            <DataCard
              label="Total Bucket Tenant"
              value={rfsStatusCount?.["Total Bucket Tenant"]}
              status={STATUS.RFS.TOTAL_BUCKET_TENANT}
              className="font-bold"
            />
            <DataCard
              label="On Review by Tenant"
              value={rfsStatusCount?.["On Review by Tenant"]}
              status={STATUS.RFS_SUBMISSIONS_VALUES.TENANT_REVIEW}
            />
            <DataCard
              label="Approved by Tenant"
              value={rfsStatusCount?.["Approved by Tenant"]}
              status={STATUS.RFS_SUBMISSIONS_VALUES.TENANT_APPROVE}
            />
          </div>

          {/* Row 2 - Bagian Total Bucket Linknet */}
          <div className="flex w-full gap-4">
            <DataCard
              label="Total Bucket Linknet"
              value={rfsStatusCount?.["Total Bucket Linknet"]}
              status={STATUS.RFS.TOTAL_BUCKET_LINKNET}
              className="font-bold"
            />
            <DataCard
              label="In Review"
              value={rfsStatusCount?.["In Review"]}
              status={STATUS.RFS_SUBMISSIONS.REVIEW_BY_LN}
            />
            <DataCard
              label="Processing HPM"
              value={rfsStatusCount?.["Generate HPDB"]}
              status={STATUS.RFS_SUBMISSIONS_VALUES.PROCESSING_HPM}
            />
            <DataCard
              label="Prepare Submit to Tenant"
              value={rfsStatusCount?.["Prepare Submit To Tenant"]}
              status={STATUS.RFS_SUBMISSIONS_VALUES.PREPARE_SUBMIT_TO_TENANT}
            />
            <DataCard
              label="Rejected by Tenant to Linknet"
              value={rfsStatusCount?.["Rejected by Tenant to Linknet"]}
              status={
                STATUS.RFS_SUBMISSIONS_VALUES.REJECTED_BY_TENANT_TO_LINKNET
              }
            />
          </div>

          {/* Row 3 - Bagian Total Bucket Vendor */}
          <div className="flex w-full gap-4">
            <DataCard
              label="Total Bucket Vendor"
              value={rfsStatusCount?.["Total Bucket Vendor"]}
              status={STATUS.RFS.TOTAL_BUCKET_VENDOR}
              className="font-bold"
            />
            <DataCard
              label="Draft (Invalid)"
              value={rfsStatusCount?.["Draft (Invalid)"]}
              status={STATUS.RFS_SUBMISSIONS.REVISION_DATA}
            />
            <DataCard
              label="Prepare & Validate"
              value={rfsStatusCount?.["Prepare & Validate"]}
              status={STATUS.RFS_SUBMISSIONS.PREPARATION_DATA}
            />
            <DataCard
              label="Rejected"
              value={rfsStatusCount?.Rejected}
              status={STATUS.RFS_SUBMISSIONS.REJECTED_DOCUMENT_RFS}
            />
            <DataCard
              label="Rejected by Tenant to Vendor"
              value={rfsStatusCount?.["Rejected by Tenant to Vendor"]}
              status={
                STATUS.RFS_SUBMISSIONS_VALUES.REJECTED_BY_TENANT_TO_VENDOR
              }
            />
          </div>
        </div>
      </ol>

      <RFSTrendingSubmission
        selectedPICLinknets={selectedPICLinknets}
        selectedPICVendors={selectedPICVendors}
        selectedTenantNames={selectedTenantNames}
        selectedVendorNames={selectedVendorNames}
        selectedDateMonthYear={selectedDateMonthYear}
        isVendor={isVendor}
        companyCode={profile?.companyCode}
      />
    </>
  );
}
